import { useState } from 'react';
import type { UnifiedMessage } from '../../types';
import { formatTimestamp } from './constants';

export function TabButton({
  active,
  disabled,
  onClick,
  children,
  tooltip,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  tooltip?: string;
}): JSX.Element {
  const btn = (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'px-4 py-2 text-sm font-medium transition-colors rounded-t-lg',
        active
          ? 'bg-surface-elevated text-text-primary border-b-2 border-accent-brand'
          : 'text-text-secondary hover:text-text-primary hover:bg-surface-subtle',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
      ].join(' ')}
    >
      {children}
    </button>
  );

  if (tooltip && disabled) {
    return (
      <div className="relative group">
        {btn}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-surface-subtle text-text-secondary rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          {tooltip}
        </div>
      </div>
    );
  }

  return btn;
}

export function CollapsibleSection({
  title,
  badge,
  defaultOpen = true,
  children,
}: {
  title: string;
  badge?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-border-subtle rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between bg-surface-elevated hover:bg-surface-subtle transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 text-text-secondary transition-transform ${isOpen ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="font-medium text-text-primary">{title}</span>
          {badge}
        </div>
      </button>
      {isOpen && <div className="px-4 py-3 bg-surface-base">{children}</div>}
    </div>
  );
}

export function EvidenceSnippets({ snippets }: { snippets: string[] }): JSX.Element | null {
  const [showAll, setShowAll] = useState(false);

  if (snippets.length === 0) return null;

  const displayed = showAll ? snippets : snippets.slice(0, 2);

  return (
    <div className="mt-2 space-y-1">
      {displayed.map((snippet, i) => (
        <div key={i} className="text-xs text-text-secondary italic pl-3 border-l-2 border-border-subtle">
          "{snippet}"
        </div>
      ))}
      {snippets.length > 2 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-xs text-accent-brand hover:underline"
        >
          Показать ещё {snippets.length - 2}
        </button>
      )}
    </div>
  );
}

export function MessagePreview({ message }: { message: UnifiedMessage }): JSX.Element {
  const directionBadge = {
    in: 'bg-accent-brand/20 text-accent-brand',
    out: 'bg-accent-success/20 text-accent-success',
    system: 'bg-surface-subtle text-text-secondary',
    unknown: 'bg-accent-warning/20 text-accent-warning',
  }[message.direction];

  return (
    <div className="text-sm py-2 border-b border-border-subtle last:border-b-0">
      <div className="flex items-center gap-2 mb-1">
        <span className={`px-1.5 py-0.5 text-xs rounded ${directionBadge}`}>
          {message.direction === 'in' ? 'Вх' : message.direction === 'out' ? 'Исх' : message.direction === 'system' ? 'Сист' : '?'}
        </span>
        <span className="font-medium text-text-primary">{message.author}</span>
        {message.timestamp && (
          <span className="text-xs text-text-secondary">{formatTimestamp(message.timestamp)}</span>
        )}
      </div>
      <div className="text-text-secondary line-clamp-2">{message.text}</div>
    </div>
  );
}
